// src/Modal.js

import { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import Player from './Player';
import Sidebar from './Sidebar';
import ContentColumn from './ContentColumn';
import Recommendations from './Recommendations';

// Import the new custom hooks
import useTVData from '../hooks/useTVData';
import useAddToRecent from '../hooks/useAddToRecent';

export default function Modal({ item, onClose, onAddToRecent, onItemSelect }) {
  // Set the default server to 'vidsrc.cc'
  const [currentServer, setCurrentServer] = useState('vidsrc.cc');
  const [embedUrl, setEmbedUrl] = useState('');

  // Use the custom hook for all TV data logic
  const {
    seasons,
    episodes,
    selectedSeason,
    setSelectedSeason,
    selectedEpisode,
    setSelectedEpisode,
    isLoading,
    isEpisodeReleased,
  } = useTVData(item);

  // Use the custom hook for "add to recent" logic
  useAddToRecent({ item, selectedSeason, selectedEpisode, onAddToRecent });

  // Effect to set the embed URL for the player
  useEffect(() => {
    if (!item) return;
    document.body.style.overflow = 'hidden';

    const type = item.media_type === 'movie' ? 'movie' : 'tv';
    const seasonEp = type === 'tv' ? `/${selectedSeason}/${selectedEpisode}` : '';

    // Using a map for clarity
    const serverUrlMap = {
      'vidsrc.cc': `https://vidsrc.cc/v2/embed/${type}/${item.id}${seasonEp}`,
      'vidsrc.me': `https://vidlink.pro/${type}/${item.id}${seasonEp}`,
      'default': `https://player.videasy.net/${type}/${item.id}${seasonEp}`
    };

    setEmbedUrl(serverUrlMap[currentServer] || serverUrlMap['default']);

    return () => { document.body.style.overflow = 'unset'; };
  }, [item, selectedSeason, selectedEpisode, currentServer]);

  // Episode navigation handlers
  const handlePrevEpisode = () => {
    const prevEpisode = episodes.find(ep => ep.episode_number === selectedEpisode - 1);
    if (prevEpisode && isEpisodeReleased(prevEpisode.air_date)) {
      setSelectedEpisode(s => s - 1);
    }
  };

  const handleNextEpisode = () => {
    const nextEpisode = episodes.find(ep => ep.episode_number === selectedEpisode + 1);
    if (nextEpisode && isEpisodeReleased(nextEpisode.air_date)) {
      setSelectedEpisode(s => s + 1);
    }
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto modal-no-scrollbar">
      <div className="bg-gray-900 rounded-none sm:rounded-xl w-full max-w-full sm:max-w-5xl h-auto max-h-[calc(100dvh-10px)] overflow-y-auto modal-no-scrollbar shadow-2xl mt-1 sm:mt-0 relative">
        {/* Sticky close button */}
        <div className="sticky top-0 right-0 z-50 w-full pointer-events-none">
          <div className="relative h-0">
            <button
              onClick={onClose}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 pointer-events-auto text-white bg-red-800 hover:bg-red-700 rounded-full p-2 transition-all duration-200 shadow-lg"
            >
              <FiX className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        <div className="relative">
          <Player
            embedUrl={embedUrl}
            episodes={episodes}
            selectedEpisode={selectedEpisode}
            handlePrevEpisode={handlePrevEpisode}
            handleNextEpisode={handleNextEpisode}
            isEpisodeReleased={isEpisodeReleased}
            selectedSeason={selectedSeason}
          />

          <div className="p-4 sm:p-6">
            <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
              <div className="md:w-2/3 space-y-4 sm:space-y-6">
                <ContentColumn {...{ item, currentServer, setCurrentServer, seasons, episodes, selectedSeason, setSelectedSeason, selectedEpisode, setSelectedEpisode, isLoading, isEpisodeReleased }} />
              </div>
              <div className="md:w-1/3">
                <Sidebar item={item} currentServer={currentServer} selectedSeason={selectedSeason} selectedEpisode={selectedEpisode} episodes={episodes} />
              </div>
            </div>

            <div className="mt-6 border-t border-gray-700 pt-6">
              <Recommendations
                item={item}
                onItemSelect={onItemSelect} // Pass onItemSelect directly now
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
